import { inject, Injectable, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExternalService } from '@finra/angular-data-collection-sdk';
import { BehaviorSubject, forkJoin, map, Observable, of, catchError } from 'rxjs';
import { InstanceService } from './instance.service';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { DisplayRulesService, FilingStatusDisplayRules } from './display-rules.service';
import { GlobalContext } from './global-context.service';
import { ProfilesService } from './profile.service';
import { OverlayService, scrollFieldIntoView } from '@finra/forms';
import { AbstractAppBaseEnv, AbstractCompletenessCheckService, NavigationData } from '@finra/rampdi-forms-common';

@Injectable({
  providedIn: 'root',
})
export class CompletenessCheckService extends AbstractCompletenessCheckService {
  initialCheckValue: boolean = false;
  http = inject(HttpClient);
  private _DRS = inject(DisplayRulesService);
  displayRules$: Observable<FilingStatusDisplayRules> = this._DRS.displayRules$;
  globalContext = inject(GlobalContext);
  profilesService = inject(ProfilesService);
  private window = window;
  private relationshipCheckResult: any = null;
  openConcurrencyModal$ = new BehaviorSubject(false);

  // Override: rampdi-forms-common calls validateUsingPOST which was renamed to validate in SDK 2.20.0
  //override validateInstance(instanceData: any): Observable<{ success: boolean; data: any }> {
  //const instance = deepClone(instanceData);
  //instance.instanceMetadata.status = 'SUBMITTED';
  //instance.instanceId = instance.id;
  //return (this as any)._es
  //.validate(instance.templateNamespace, instance.templateName, instance.templateVersion, instance)
  //.pipe(
  //map((data) => ({ success: true, data })),
  //catchError((err) => of({ success: false, data: err })),
  //);
  //}
  disclosureDeleteModal?: TemplateRef<any>;
  setDisclosureDeleteModal(template: TemplateRef<any>) {
    this.disclosureDeleteModal = template;
  }
  setAppComponent(component: any) {
    this.appComponent = component;
  }
  setFormSelector(component: any) {
    this.formSelector = component;
  }
  concurrencyDetected: boolean = false;
  appComponent: any;
  formSelector: any;

  constructor(
    private es: ExternalService,
    public route: ActivatedRoute,
    public router: Router,
    private instanceService: InstanceService,
    private OS: OverlayService,
  ) {
    super(es, route);
  }

  preprocessNavData(navData: NavigationData[]) {
    return navData;
  }

  private get environment(): AbstractAppBaseEnv {
    return environment;
  }

  refreshFormData() {
    // Force the form to refresh by updating the instance service and triggering change detection
    this.instanceService.currentInstance = { ...this.instanceService.currentInstance };
  }

  gotoField(selectors: string[]) {
    setTimeout(async () => {
      try {
        console.log('selectors', selectors);
        if (selectors.length > 1) {
          const arraySelector: any = selectors.shift();
          const formControlIndex = +selectors.shift();
          const formControlKey = arraySelector.replaceAll('-', '.');
          //Since disclosures explanations can have multiple associations, we need to only trigger one event, otherwise multiple modals are opened.
          if (formControlKey?.includes('disclosureExplanations')) {
            if (selectors?.includes('associations')) {
              this.appComponent.selectedDisclosureIndex = null;
              this.appComponent.currentSelectDisclosure = {
                associations: [],
              };
              const isDisabled = new BehaviorSubject<boolean>(true);
              let componentModel =
                this.instanceService.currentInstance?.collectedData?.disclosureQuestions?.disclosures
                  ?.disclosureExplanations || [];
              let prevComponentModel = deepClone(
                this.instanceService.currentInstance?.collectedData?.disclosureQuestions?.disclosures
                  ?.disclosureExplanations || [],
              );

              this.appComponent.modalDeletionReason = '';
              const specialChars = /^((?!.*<(?! *>).*>)(?! +$|\r?\n+$).+|\r?\n)*$/;
              const initialDeleteReason =
                componentModel[this.appComponent.selectedDisclosureIndex]?.disclosureExplanationDeleteReason;
              if (
                !initialDeleteReason ||
                initialDeleteReason.trim().length === 0 ||
                specialChars.test(initialDeleteReason)
              ) {
                isDisabled.next(true);
              } else {
                isDisabled.next(false);
              }
              let validationError$ = new BehaviorSubject<boolean>(false);
              const validateDeleteReason = (event) => {
                const validateInput = (value: string) => {
                  return value && value.trim().length > 0 && specialChars.test(value);
                };
                const value = event?.target?.value;
                this.appComponent.modalDeletionReason = value;

                const selectedItem = componentModel[this.appComponent.selectedDisclosureIndex];
                const isNewStatus = selectedItem?.disclosureExplanationStatus === 'CREATE';

                if (!value || !validateInput(value) || !selectedItem) {
                  if (!value || value.trim().length === 0) {
                    this.appComponent.disclosureDeleteReasonError = 'A reason for deleting an explanation is required.';
                    validationError$.next(true);
                  } else if (!specialChars.test(value)) {
                    this.appComponent.disclosureDeleteReasonError =
                      'HTML content is not allowed. Please remove any HTML-formatted text (e.g., <p>).';
                    validationError$.next(true);
                  } else {
                    validationError$.next(false);
                  }
                  isDisabled.next(!isNewStatus);
                  return;
                } else {
                  selectedItem.disclosureExplanationDeleteReason = value;
                  isDisabled.next(false);
                  validationError$.next(false);
                  this.appComponent.disclosureDeleteReasonError = '';
                }
              };

              try {
                await this.ensureEnumOptions();
              } catch (error) {
                console.error('Failed to ensure enum options:', error);
              }
              this.OS.open(this.disclosureDeleteModal, {
                theme: 'info',
                title: 'Delete Existing Explanation',
                buttonJustifyContent: 'end',
                templateData: {
                  model: componentModel,
                  isDisabled,
                  validateDeleteReason: validateDeleteReason,
                  validationError: validationError$,
                },
                buttons: [
                  { name: 'Cancel', value: false, theme: 'secondary', 'aria-label': 'Cancel explanation deletion' },
                  {
                    name: 'Delete Selected Explanation',
                    value: true,
                    theme: 'primary',
                    isDisabled: isDisabled.asObservable(),
                    'aria-label': 'Delete selected explanation',
                  },
                ],
              })
                .afterClosed$.toPromise()
                .then((e) => {
                  if (e.data) {
                    const selectedItem = componentModel[this.appComponent.selectedDisclosureIndex];
                    if (selectedItem?.disclosureExplanationStatus === 'CREATE') {
                      componentModel.splice(this.appComponent.selectedDisclosureIndex, 1);
                    } else {
                      componentModel[this.appComponent.selectedDisclosureIndex].disclosureExplanationStatus = 'REMOVE';
                      componentModel[this.appComponent.selectedDisclosureIndex].updatedDate = new Date().toISOString();
                      componentModel[this.appComponent.selectedDisclosureIndex].disclosureExplanationAmendmentReason =
                        null;
                    }
                    // Refresh form data to reflect the updated instance
                    this.refreshFormData();
                  } else {
                    if (this.appComponent?.selectedDisclosureIndex) {
                      componentModel[this.appComponent.selectedDisclosureIndex].disclosureExplanationDeleteReason =
                        prevComponentModel?.[
                          this.appComponent.selectedDisclosureIndex
                        ]?.disclosureExplanationDeleteReason;
                    }
                  }
                });
              return;
            }

            const model =
              this.instanceService.currentInstance?.collectedData?.disclosureQuestions?.disclosures
                ?.disclosureExplanations;
            const targetItem = model?.[formControlIndex];
            let modalOpened = false;

            if (targetItem && model) {
              const questionTables = Array.from(
                document.querySelectorAll(`formly-field.explanation-assoc fin-array-type`),
              );
              for (const el of questionTables) {
                if (modalOpened) break;
                const rows = el.shadowRoot?.querySelector('finra-table')?.shadowRoot?.querySelector('tbody')?.children;
                if (rows && rows.length > 0) {
                  const filteredModel = model.filter((exp) => exp.type === 'question');
                  const sortedModel = [...filteredModel].sort(
                    (a, b) => new Date(b.updatedDate || 0).getTime() - new Date(a.updatedDate || 0).getTime(),
                  );
                  const targetRowIndex = sortedModel.findIndex((item) => item === targetItem);

                  if (targetRowIndex >= 0 && rows[targetRowIndex]) {
                    const icon = rows[targetRowIndex].querySelector('finra-icon[title="Edit"]');
                    if (icon) {
                      (icon as HTMLElement).click();
                      modalOpened = true;
                      break;
                    }
                  }
                }
              }

              if (!modalOpened) {
                const categoryTables = Array.from(
                  document.querySelectorAll(`formly-field.category-assoc fin-array-type`),
                );
                for (const el of categoryTables) {
                  if (modalOpened) break;
                  const rows = el.shadowRoot
                    ?.querySelector('finra-table')
                    ?.shadowRoot?.querySelector('tbody')?.children;
                  if (rows && rows.length > 0) {
                    const filteredModel = model.filter((exp) => exp.type === 'category');
                    const sortedModel = [...filteredModel].sort(
                      (a, b) => new Date(b.updatedDate || 0).getTime() - new Date(a.updatedDate || 0).getTime(),
                    );
                    const targetRowIndex = sortedModel.findIndex((item) => item === targetItem);

                    if (targetRowIndex >= 0 && rows[targetRowIndex]) {
                      const icon = rows[targetRowIndex].querySelector('finra-icon[title="Edit"]');
                      if (icon) {
                        (icon as HTMLElement).click();
                        modalOpened = true;
                        break;
                      }
                    }
                  }
                }
              }
            }
          } else {
            const isDocument = formControlKey?.includes('documents');
            const isDisclosure = formControlKey?.includes('disclosureQuestions');

            Array.from(document.querySelectorAll(`formly-field.${arraySelector}`)).forEach((formlyField: any) => {
              const el = formlyField.querySelector('fin-array-type');

              if (isDocument || isDisclosure) {
                // Get model from instance service
                const model = isDocument
                  ? this.instanceService.currentInstance?.collectedData?.documents
                  : this.instanceService.currentInstance?.collectedData?.disclosureQuestions?.disclosures
                      ?.disclosureExplanations;

                if (!model) {
                  return;
                }

                const targetItem = model[formControlIndex];

                // Find the table and rows
                const table = el?.shadowRoot?.querySelector('finra-table');
                const tbody = table?.shadowRoot?.querySelector('tbody');
                const rows = tbody?.children;

                // Sort model by updatedDate descending to match table display
                const sortedModel = [...model].sort((a, b) => {
                  const dateA = new Date(a.updatedDate || 0).getTime();
                  const dateB = new Date(b.updatedDate || 0).getTime();
                  return dateB - dateA;
                });

                // Find target item position in sorted array
                const targetRowIndex = sortedModel.indexOf(targetItem);

                if (rows && targetRowIndex >= 0 && rows[targetRowIndex]) {
                  const row = rows[targetRowIndex];
                  const cells = row.querySelectorAll('td');

                  for (let i = 0; i < cells.length; i++) {
                    const icon = cells[i].querySelector('finra-icon[title="Edit"]');
                    if (icon) {
                      (icon as HTMLElement).click();
                      break;
                    }
                  }
                }
              } else {
                el?.dispatchEvent(new CustomEvent('edit', { detail: { formControlIndex, formControlKey, selectors } }));
              }
            });
          }
        } else if (selectors[0] === 'others') {
          const globalContextStore = this.globalContext?.store?.();
          const permissions = globalContextStore?.permissions || [];
          if (
            (this._DRS.userPermissions.isIndividualUser || this._DRS.userPermissions.isCompanyUser) &&
            this.concurrencyDetected
          ) {
            this.setCompletenessCheckData({ open: false, loading: false });
            this.openConcurrencyModal$.next(true);
            return;
          }
          if (permissions.includes('CompanyManageCompanyRelationships') || permissions.includes('Administrator')) {
            const subjectId = this.instanceService.currentInstance.instanceMetadata?.target?.['subjectId'];
            const baseUrl = `${environment.urls.individualSummary}/${subjectId}/`;
            const queryParams = '?activeTab=relationships';
            const fullUrl = `${baseUrl}${queryParams}`;
            window.open(fullUrl, '_self');
          }
        } else {
          const selector = `formly-field.${selectors.shift()}`;
          const element = document.querySelector(selector);
          element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        console.error('Error in gotoField:', error);
      }
    });
  }

  getSelectors(item: { navData: NavigationData; pointer: string }, section) {
    if (section.id === 'others') {
      item.navData = { id: 'others' };
    }
    const { modelPath = [item.navData.id] } = deepClone(item?.navData);
    let skipModelPath = false;
    const classNameList = [];
    let className = [];
    const pathSegments = item.pointer.split('/').slice(1);
    for (const prop of pathSegments) {
      if (!skipModelPath && prop === modelPath.shift()) {
        continue;
      } else {
        skipModelPath = true;
        if (this.isArrayIndex(prop)) {
          classNameList.push(className.join('-'), prop);
          className = [];
        } else {
          className.push(prop);
        }
      }
    }
    if (className.length) {
      classNameList.push(className.join('-'));
    }
    if (section.id === 'others') {
      classNameList.push('others');
    }
    return classNameList;
  }

  onValidateCCOpened() {
    this.setCompletenessCheckData({ open: true, loading: true });

    const subjectId = this.instanceService.currentInstance.instanceMetadata?.target?.['subjectId'];
    const companyEntityId = this.instanceService.currentInstance.instanceMetadata?.target?.['entityId'];

    // Need to deep clone the currentInstance data to avoid CC error on cloudwell flow
    const instanceData = deepClone(this.instanceService?.currentInstance);
    if (instanceData?.collectedData?.cloudwell) {
      delete instanceData.collectedData.cloudwell;
    }

    // Prepare observables
    const ccRequest$ = this.validateInstance(instanceData);
    // Wait for both to complete
    forkJoin({
      ccResult: ccRequest$,
    }).subscribe(({ ccResult }) => {
      // Process CC result
      if (ccResult.success) {
        this.completenessCheckOthersMessage(ccResult);
      } else if (ccResult?.data?.status === 400) {
        this.completenessCheckOthersMessage(ccResult);
      } else if (ccResult?.data?.status === 403) {
        this.setCompletenessCheckData({ open: false, loading: false });
        if (sessionStorage.getItem('cc403Reload')) {
          sessionStorage.removeItem('cc403Reload');
          return;
        }
        globalThis.finformUnsavedChanges = false;
        sessionStorage.setItem('cc403Reload', 'true');
        this.window.location.reload();
      } else {
        this.setCompletenessCheckData({ open: true, fail: true });
      }
    });
  }

  checkConcurrencyDetected(res) {
    Object.keys(res?.data?.error?.errorDetails || {}).forEach((key) => {
      const value = res?.data?.error?.errorDetails[key];
      if (value[0].includes('Updates to your MU1 have been submitted since creating this draft.')) {
        this.concurrencyDetected = true;
      }
    });
  }

  completenessCheckOthersMessage(res) {
    // Forcing Concurrency issue to be resolved first
    this.checkConcurrencyDetected(res);
    if (this.concurrencyDetected) {
      this.setCompletenessCheckData(
        this.classifyErrorList(res?.data?.error?.errorDetails, res?.data?.error?.warningDetails),
      );
      return;
    }

    const subjectId = this.instanceService.currentInstance.instanceMetadata?.target?.['subjectId'];

    // Check relationship for company users using cached result
    if (subjectId && this._DRS.userPermissions.isCompanyUser && this.relationshipCheckResult) {
      const ccMessage =
        'In order to submit this MU1 there must be an active company relationship between the individual and the company creating the MU1 and a worker classification must be provided.';

      if (!this.relationshipCheckResult.isActiveRelationshipExists) {
        if (res?.data?.error) {
          res.data.error.errorDetails[''] = [ccMessage];
        } else {
          res.data = {
            error: {
              errorDetails: {
                '': [ccMessage],
              },
            },
          };
        }
      }
    }

    // Process CC result
    this.processCompletenessCheckResult(res);
  }

  private processCompletenessCheckResult(res) {
    const hasErrors = Object.keys(res?.data?.error?.errorDetails || {}).length > 0;
    const hasWarnings = Object.keys(res?.data?.error?.warningDetails || {}).length > 0;

    if (hasErrors || hasWarnings) {
      this.setCompletenessCheckData({
        ...this.classifyErrorList(res?.data?.error?.errorDetails, res?.data?.error?.warningDetails),
        fail: false,
        loading: false,
      });
    } else if (res.success) {
      this.setCompletenessCheckData({
        open: true,
        success: true,
        count: { error: 0, warning: 0, total: 0 },
        lastUpdated: Date.now(),
        successMessage: `${this.environment.formName} completeness check passed successfully`,
        loading: false,
      });
    }
  }

  private async ensureEnumOptions() {
    if (this.formSelector?.disclosureExplanationStatusOptions?.length) return;

    // Try to get from already built field
    const field = this.formSelector?.form?.get('collectedData.disclosureQuestions.disclosures.disclosureExplanations');
    const columns = field?.options?.fieldArray?.fieldGroup?.[0]?.props?.columns;

    if (columns) {
      columns.forEach((col) => {
        if (col.key === 'disclosureExplanationStatus' && col.props?.options) {
          this.formSelector.disclosureExplanationStatusOptions = [...col.props.options];
        }
        if (col.key === 'dispositionType' && col.props?.options) {
          this.formSelector.dispositionTypeOptions = [...col.props.options];
        }
      });
    }

    // If still not available, fetch from API
    if (!this.formSelector?.disclosureExplanationStatusOptions?.length) {
      try {
        const response = await this.http
          .get<any>(
            `${environment.urls.apiReference}/reference-data?contentType=application/schema+json&referenceKey=DISC_EXPL_STATUS&activeData=true`,
          )
          .toPromise();
        const oneOf = response?.oneOf || [];
        this.formSelector.disclosureExplanationStatusOptions = oneOf.map((item) => ({
          value: item.const,
          label: item.title,
        }));
      } catch (error) {
        console.error('Failed to load disclosure status options:', error);
      }
    }

    if (!this.formSelector?.dispositionTypeOptions?.length) {
      try {
        const response = await this.http
          .get<any>(
            `${environment.urls.apiReference}/reference-data?contentType=application/schema+json&referenceKey=DISC_DISP_TYPES&activeData=false`,
          )
          .toPromise();
        const oneOf = response?.oneOf || [];
        this.formSelector.dispositionTypeOptions = oneOf.map((item) => ({ value: item.const, label: item.title }));
      } catch (error) {
        console.error('Failed to load disposition type options:', error);
      }
    }
  }
}

export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}
